import type { LeadScoreResult } from './leadScore'
import { quizData } from '../data/quizData'

/**
 * 🔧 SETUP — Reemplaza este URL con tu endpoint:
 *
 * Opciones recomendadas (todas funcionan con POST + JSON):
 *   1. Formspree:  https://formspree.io/f/TU_ID
 *   2. Zapier Webhook:  https://hooks.zapier.com/hooks/catch/...
 *   3. Make (Integromat) Webhook:  https://hook.eu1.make.com/...
 *   4. Tu propio backend
 *
 * Mientras no configures un endpoint real, el lead se mostrará en la
 * consola del navegador (F12 → Console) Y se abrirá un mailto opcional.
 */
const LEAD_WEBHOOK_URL = 'https://formspree.io/f/xwvzkezn'
const FALLBACK_EMAIL = 'leads@ainacademy.com' // se usa solo en modo mailto

export interface LeadPayload {
  name: string
  email: string
  score: LeadScoreResult
  answers: Record<number, string | string[]>
  submittedAt: string
  source: string
}

export function buildLeadPayload(
  name: string,
  email: string,
  score: LeadScoreResult,
  answers: Record<number, string | string[]>,
): LeadPayload {
  return {
    name,
    email,
    score,
    answers,
    submittedAt: new Date().toISOString(),
    source: 'AIN Academy — Quiz IA Project Managers',
  }
}

/**
 * Envia el lead a tu endpoint. Si no hay endpoint configurado,
 * loguea en consola para que puedas verificar el flow.
 */
export async function sendLead(
  payload: LeadPayload,
): Promise<{ ok: boolean; mode: 'webhook' | 'console' }> {
  // Modo consola si no hay endpoint configurado
  if (!LEAD_WEBHOOK_URL || LEAD_WEBHOOK_URL === 'YOUR_WEBHOOK_URL_HERE') {
    // eslint-disable-next-line no-console
    console.group(
      '%c📩 LEAD CAPTURADO (modo consola — conecta un webhook para envío real)',
      'background:#EA4E3D;color:white;padding:4px 8px;border-radius:4px;font-weight:bold',
    )
    // eslint-disable-next-line no-console
    console.log('Nombre:', payload.name)
    // eslint-disable-next-line no-console
    console.log('Email:', payload.email)
    // eslint-disable-next-line no-console
    console.log(
      'Score:',
      payload.score.score,
      '/100 —',
      payload.score.tierLabel,
    )
    // eslint-disable-next-line no-console
    console.log('Perfil:', payload.score.profile)
    // eslint-disable-next-line no-console
    console.log('Payload completo:', payload)
    // eslint-disable-next-line no-console
    console.groupEnd()
    return { ok: true, mode: 'console' }
  }

  // Estructuramos el body para que Formspree lo muestre legible en el email:
  // campos planos al nivel superior (subject, name, email, score…) + JSON crudo.
  const flatBody = {
    _subject: `🎯 Nuevo Lead AIN — Score ${payload.score.score}/100 (${payload.score.tier.toUpperCase()})`,
    _replyto: payload.email,
    name: payload.name,
    email: payload.email,
    score: `${payload.score.score} / 100`,
    tier: payload.score.tierLabel,
    profile: payload.score.profile,
    submittedAt: payload.submittedAt,
    source: payload.source,
    message: formatLeadAsEmail(payload),
    // datos crudos por si los conectas a Zapier/Make/CRM más adelante
    raw_score_object: payload.score,
    raw_answers: payload.answers,
  }

  try {
    const res = await fetch(LEAD_WEBHOOK_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(flatBody),
    })
    return { ok: res.ok, mode: 'webhook' }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('❌ Error enviando lead:', err)
    return { ok: false, mode: 'webhook' }
  }
}

/**
 * Genera un cuerpo de email legible para humanos (útil para Formspree,
 * Zapier o cualquier integración que reciba el JSON y lo reenvíe por email).
 */
export function formatLeadAsEmail(payload: LeadPayload): string {
  const { name, email, score, answers, submittedAt } = payload
  const lines: string[] = []
  lines.push(`Nuevo lead — AIN Academy (Quiz IA Project Managers)`)
  lines.push(``)
  lines.push(`Nombre: ${name}`)
  lines.push(`Email: ${email}`)
  lines.push(`Fecha: ${submittedAt}`)
  lines.push(``)
  lines.push(`──────── SCORING ────────`)
  lines.push(`Score: ${score.score} / 100`)
  lines.push(`Tier: ${score.tierLabel}`)
  lines.push(`Perfil: ${score.profile}`)
  lines.push(``)
  lines.push(`──────── RESPUESTAS ────────`)
  Object.entries(answers).forEach(([qIdx, ans]) => {
    const q = quizData[Number(qIdx)]
    if (!q) return
    const tagList = Array.isArray(ans) ? ans : [ans]
    const labels = tagList.map((tag) => {
      const opt = q.options.find((o) => o.tag === tag)
      return opt ? opt.text : tag
    })
    lines.push(`${Number(qIdx) + 1}. ${q.question.replace(/\n/g, ' ')}`)
    lines.push(`   → ${labels.join(' | ')}`)
  })
  return lines.join('\n')
}
